import { CheckCircle2, Clock3, HelpCircle, LoaderCircle, RefreshCw, XCircle } from 'lucide-react'
import type { ReactNode } from 'react'

import { Button, DiagnosticCopy } from '@krp/ui-kit'

import { useNow } from './useNow'
import type { OperationVm, Tone } from '../model/types'

const ICONS: Readonly<Record<Tone, ReactNode>> = {
  neutral: <Clock3 size={16} aria-hidden="true" />,
  warning: <HelpCircle size={16} aria-hidden="true" />,
  danger: <XCircle size={16} aria-hidden="true" />,
  success: <CheckCircle2 size={16} aria-hidden="true" />,
}

/** Ход запроса и ответы службы; ожидание не выдаётся за выполняющийся запрос. */
export function OperationPanel({
  operation,
  onCheck,
  onReconcile,
  onDismiss,
}: {
  operation: OperationVm
  onCheck: () => void
  onReconcile: () => void
  onDismiss: () => void
}) {
  const now = useNow(1000, !operation.canDismiss)
  const nextIn = operation.nextCheckAt == null ? null : Math.max(0, Math.ceil((operation.nextCheckAt - now) / 1000))
  const requestActive = operation.phase === 'sending' || operation.checking || operation.reconciling
  const requestText = operation.phase === 'sending' ? 'Отправляем команду. Ждём ответ службы…'
    : operation.phase === 'not-found' ? 'Служба ответила: команда не получена.'
    : operation.checking || operation.reconciling ? 'Запрос к службе отправлен. Ждём ответ…'
    : operation.canDismiss ? 'Ответ получен. Проверка завершена.'
    : nextIn !== null && nextIn > 0 ? `Следующий запрос через ${nextIn} с`
    : nextIn === 0 ? 'Ожидаем запуска следующего запроса…'
    : 'Ожидаем следующую проверку статуса.'
  const tone = operation.tone === 'success' ? 'neutral' : operation.tone
  return (
    <section className="aqsi-panel" data-tone={tone} aria-label="Команда смены" data-phase={operation.phase}>
      <h3>
        {ICONS[operation.tone]}
        {operation.kindText}: {operation.stateText}
      </h3>
      <p className="aqsi-card__text" role="status" aria-label="Ход запроса">
        {requestActive && <LoaderCircle className="aqsi-spinner" size={16} aria-hidden="true" />} {requestText}
      </p>
      {operation.details.map((line) => (
        <p key={line} className="aqsi-card__text">
          {line}
        </p>
      ))}
      {(operation.tone === 'warning' || operation.tone === 'danger' || operation.manualRequired) && <DiagnosticCopy text={[operation.kindText, operation.stateText, ...operation.details, `Номер операции: ${operation.operationId}`].join('\n')} />}
      <div className="aqsi-card__actions">
        {operation.canReconcile && (
          <Button icon={operation.reconciling ? <LoaderCircle className="aqsi-spinner" size={14} aria-hidden="true" /> : <RefreshCw size={14} aria-hidden="true" />} busy={operation.reconciling} onClick={onReconcile}>
            Проверить результат
          </Button>
        )}
        {operation.canCheck && !operation.canReconcile && (
          <Button icon={operation.checking ? <LoaderCircle className="aqsi-spinner" size={14} aria-hidden="true" /> : <RefreshCw size={14} aria-hidden="true" />} busy={operation.checking} onClick={onCheck}>
            Проверить статус команды
          </Button>
        )}
        {operation.canDismiss && (
          <Button variant="ghost" onClick={onDismiss}>
            Понятно
          </Button>
        )}
      </div>
      <details className="aqsi-caption">
        <summary>Подробнее</summary>
        Номер операции: {operation.operationId}
      </details>
    </section>
  )
}
